import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MessageCircle, Send, RefreshCw } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QUICK_REPLIES = [
  'Your loan has been approved 🎉',
  'We are still waiting for your documents — please upload them on your dashboard.',
  'Please approve the DebiCheck request sent to your phone so we can pay out your loan.',
  'Please sign your loan documents so we can proceed.',
  'Your payment was received — thank you!',
  'Your payout has been processed and should reflect shortly.',
];

let cachedReplies = null;

export const QuickReplyChips = ({ onPick, idPrefix = 'quick-reply', getAuthHeader }) => {
  const [replies, setReplies] = useState(cachedReplies || QUICK_REPLIES);
  useEffect(() => {
    if (cachedReplies || !getAuthHeader) return;
    axios.get(`${API_URL}/admin/chat-settings`, getAuthHeader())
      .then(r => { cachedReplies = r.data.quick_replies; setReplies(cachedReplies); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="flex flex-wrap gap-1.5 pb-1 px-1" data-testid={`${idPrefix}-row`}>
      {replies.map((q, i) => (
        <button key={i} onClick={() => onPick(q)} data-testid={`${idPrefix}-${i}`}
          className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 hover:bg-[#0f2b4a] hover:text-white hover:border-[#0f2b4a] transition-colors whitespace-nowrap">
          {q.length > 48 ? q.slice(0, 46) + '…' : q}
        </button>
      ))}
    </div>
  );
};

// Compact chat thread for embedding in the application detail dialog
export const ChatThreadInline = ({ userId, userName, getAuthHeader }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bottomRef = useRef(null);

  const fetchThread = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/chat/${userId}`, getAuthHeader());
      setMessages(res.data);
    } catch { /* ignore */ }
    finally { setLoaded(true); }
  }, [userId, getAuthHeader]);

  useEffect(() => {
    if (!userId) return;
    fetchThread();
    const interval = setInterval(fetchThread, 15000);
    return () => clearInterval(interval);
  }, [userId, fetchThread]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'nearest' }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/admin/chat/${userId}`, { message: text }, getAuthHeader());
      setMessages(prev => [...prev, res.data]);
      setInput('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  if (!userId) return null;

  return (
    <div data-testid="app-detail-chat">
      <p className="font-semibold text-sm flex items-center gap-2 mb-2">
        <MessageCircle className="w-4 h-4" /> Live Chat ({messages.length})
      </p>
      <div className="max-h-[220px] overflow-y-auto space-y-2 pr-1" data-testid="app-detail-chat-messages">
        {loaded && messages.length === 0 && (
          <p className="text-xs text-slate-400 py-2">No chat messages with this client yet — send the first one below.</p>
        )}
        {messages.map(m => (
          <div key={m.id} className={`flex ${m.sender === 'staff' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] rounded-xl px-3 py-1.5 text-xs ${m.sender === 'staff'
              ? 'bg-[#0f2b4a] text-white rounded-br-sm'
              : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'}`}>
              {m.sender === 'staff' && <span className="text-[9px] text-white/60 block">{m.sender_name}</span>}
              <p className="whitespace-pre-wrap break-words">{m.message}</p>
              <p className={`text-[9px] mt-0.5 ${m.sender === 'staff' ? 'text-white/50' : 'text-slate-400'}`}>
                {new Date(m.created_at).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="flex items-center gap-2 mt-2">
        <input
          className="flex-1 text-xs border border-slate-200 rounded-full px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#0f2b4a]/30 bg-white"
          placeholder={`Chat with ${userName || 'client'}... (they get notified)`}
          value={input}
          maxLength={2000}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') send(); }}
          data-testid="app-detail-chat-input"
        />
        <Button size="sm" className="rounded-full bg-[#0f2b4a] h-7 px-3" disabled={!input.trim() || sending} onClick={send} data-testid="app-detail-chat-send-btn">
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
      <div className="mt-2">
        <QuickReplyChips onPick={setInput} idPrefix="detail-quick-reply" getAuthHeader={getAuthHeader} />
      </div>
    </div>
  );
};

export default function ChatPanel({ getAuthHeader }) {
  const [threads, setThreads] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = activeUser;

  const fetchThreads = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/chat/threads`, getAuthHeader());
      setThreads(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  const fetchThread = useCallback(async (userId) => {
    try {
      const res = await axios.get(`${API_URL}/admin/chat/${userId}`, getAuthHeader());
      setMessages(res.data);
    } catch { toast.error('Failed to load conversation'); }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchThreads();
    const interval = setInterval(() => {
      fetchThreads();
      if (activeRef.current) fetchThread(activeRef.current.user_id);
    }, 12000);
    return () => clearInterval(interval);
  }, [fetchThreads, fetchThread]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const openThread = (t) => {
    setActiveUser(t);
    fetchThread(t.user_id);
    setThreads(prev => prev.map(x => x.user_id === t.user_id ? { ...x, unread: 0 } : x));
  };

  const send = async () => {
    const text = input.trim();
    if (!text || !activeUser || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/admin/chat/${activeUser.user_id}`, { message: text }, getAuthHeader());
      setMessages(prev => [...prev, res.data]);
      setInput('');
      fetchThreads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  return (
    <Card data-testid="admin-chat-panel">
      <CardContent className="p-0">
        <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] min-h-[520px]">
          {/* Threads list */}
          <div className="border-r border-slate-100 flex flex-col">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <p className="font-semibold text-sm flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Client Chats</p>
              <Button variant="ghost" size="sm" onClick={fetchThreads} data-testid="chat-refresh-btn"><RefreshCw className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="flex-1 overflow-y-auto max-h-[470px]" data-testid="chat-threads-list">
              {loading ? <p className="text-center text-slate-400 text-sm py-8">Loading...</p> :
              threads.length === 0 ? <p className="text-center text-slate-400 text-sm py-8 px-4">No client chats yet. Clients message you from the chat bubble on their dashboard.</p> :
              threads.map(t => (
                <button key={t.user_id} onClick={() => openThread(t)} data-testid={`chat-thread-${t.user_id}`}
                  className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${activeUser?.user_id === t.user_id ? 'bg-blue-50/60' : ''}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm truncate">{t.user_name || t.user_email}</p>
                    {t.unread > 0 && <Badge className="bg-amber-500 text-white text-[10px] px-1.5">{t.unread}</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">
                    {t.last_sender === 'staff' ? 'You: ' : ''}{t.last_message}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{t.last_at ? new Date(t.last_at).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Conversation */}
          <div className="flex flex-col">
            {!activeUser ? (
              <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Select a chat to reply</div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-slate-100">
                  <p className="font-semibold text-sm">{activeUser.user_name}</p>
                  <p className="text-xs text-slate-400">{activeUser.user_email}</p>
                </div>
                <div className="flex-1 overflow-y-auto max-h-[380px] p-4 space-y-2 bg-slate-50/60" data-testid="chat-conversation">
                  {messages.map(m => (
                    <div key={m.id} className={`flex ${m.sender === 'staff' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] rounded-2xl px-3 py-2 text-sm ${m.sender === 'staff'
                        ? 'bg-[#0f2b4a] text-white rounded-br-sm'
                        : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'}`}>
                        {m.sender === 'staff' && <p className="text-[10px] text-white/60 mb-0.5">{m.sender_name}</p>}
                        <p className="whitespace-pre-wrap break-words">{m.message}</p>
                        <p className={`text-[10px] mt-1 ${m.sender === 'staff' ? 'text-white/50' : 'text-slate-400'}`}>
                          {new Date(m.created_at).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>
                <div className="p-3 border-t border-slate-100 space-y-2">
                  <QuickReplyChips onPick={setInput} idPrefix="panel-quick-reply" getAuthHeader={getAuthHeader} />
                  <div className="flex items-center gap-2">
                    <input
                      className="flex-1 text-sm border border-slate-200 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#0f2b4a]/30"
                      placeholder={`Reply to ${activeUser.user_name || 'client'}...`}
                      value={input}
                      maxLength={2000}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') send(); }}
                      data-testid="admin-chat-input"
                    />
                    <Button size="icon" className="rounded-full bg-[#0f2b4a] shrink-0" disabled={!input.trim() || sending} onClick={send} data-testid="admin-chat-send-btn">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
